import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import client from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // v60: try cookie first (canonical), fall back to localStorage Bearer
  // token for the ~7-day grace window where older sessions still rely on
  // localStorage. `client` already attaches both (withCredentials + header).
  const fetchMe = useCallback(async () => {
    try {
      const res = await client.get("/auth/me");
      setUser(res.data);
    } catch {
      // Either no cookie or expired/invalid token. Drop the legacy localStorage
      // copy so the next request doesn't keep sending a dead Bearer.
      localStorage.removeItem("sm_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    const res = await client.post("/auth/login", { email, password });
    // Backend now also sets the httpOnly cookie. We still cache the token
    // in localStorage so axios keeps a Bearer header attached as a redundancy
    // path (some restrictive contexts strip cookies on CORS preflights).
    if (res.data?.access_token) {
      localStorage.setItem("sm_token", res.data.access_token);
    }
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await client.post("/auth/register", payload);
    if (res.data?.access_token) {
      localStorage.setItem("sm_token", res.data.access_token);
    }
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    // Clear local state synchronously so the UI flips to logged-out
    // immediately (callers usually fire-and-forget then navigate).
    localStorage.removeItem("sm_token");
    setUser(null);
    // Cookie clearing API call happens in the background — non-blocking.
    try { await client.post("/auth/logout"); } catch { /* offline / already cleared */ }
  };

  const refresh = async () => { await fetchMe(); };

  // Memoize so consumers don't re-render on every parent render. Only the
  // values that actually change (`user`, `loading`) bust the memo — the
  // setters/methods are stable.
  const value = useMemo(
    () => ({ user, setUser, login, register, logout, loading, refresh }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
